import { useMemo } from 'react';
import { ESRI_GEOCODE_SERVICE_URL } from '@rsos/maps/interface/reverseGeocode/helpers';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import {
  CATEGORY_PRIORITY_ORDER,
  FIXED_CATEGORY_LABELS,
} from './GlobalMapSearch.constants';
import { resolveCategoryIcon } from './GlobalMapSearch.icons';
import type {
  MapCenter,
  SearchResultItem,
  TalkJSMessageSource,
} from './GlobalMapSearch.types';
import {
  formatDistance,
  getFeatureLabel,
  getFeatureSublabel,
  getSpatialObjectCenter,
  haversineDistance,
} from './GlobalMapSearch.utils';

/** Display label for each message-source variant in the dropdown sublabel. */
const MESSAGE_SOURCE_LABELS: Record<TalkJSMessageSource, string> = {
  tf911: 'Text-from-911',
  collaborator: 'Collaborator',
  agencyShare: 'Agency Share',
};

export interface PreparedItem {
  item: SearchResultItem;
  label: string;
  sublabel: string;
  distanceLabel: string;
  flatIdx: number;
  icon:
    | 'axon'
    | 'place'
    | 'gis'
    | 'static'
    | 'dynamic'
    | 'call'
    | 'transcript'
    | 'message'
    | 'alert'
    | 'rade-maf'
    | 'rade-adr';
  /**
   * Bundled URL for a category-specific silhouette icon resolved from
   * the layer/sublayer title (see `GlobalMapSearch.icons.ts`). When
   * absent, the dropdown falls back to the generic icon-by-type map.
   */
  iconUrl?: string;
  distance: number;
}

export interface ResultGroup {
  category: string;
  heading: string;
  items: PreparedItem[];
}

/**
 * Turn a dotted attribute path (`officer.full_name`, `address`) into a
 * human-readable label (`Officer Full Name`, `Address`). Underscores and
 * dots are treated as word boundaries; each word is capitalized.
 */
function humanizeFieldPath(path: string): string {
  if (!path) return '';
  return path
    .split('.')
    .map(seg =>
      seg
        .split(/[_\s]+/)
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' '),
    )
    .filter(Boolean)
    .join(' ');
}

/** Determine which display category a single result item belongs to. */
function normalizeServiceUrl(url?: string): string {
  return (url || '').trim().toLowerCase().replace(/\/+$/, '');
}

const ESRI_GEOCODER_URL = normalizeServiceUrl(ESRI_GEOCODE_SERVICE_URL);

function categoryFor(item: SearchResultItem): string {
  if (item.type === 'axon') return 'axon';
  if (item.type === 'place') {
    const source = normalizeServiceUrl(item.data._serviceUrl);
    if (!source) return 'place';
    return source === ESRI_GEOCODER_URL ? 'place-global' : 'place-local';
  }
  if (item.type === 'static') return 'static';
  if (item.type === 'dynamic') return 'dynamic';
  if (item.type === 'call') return 'call';
  if (item.type === 'transcript') return 'transcript';
  if (item.type === 'message') return 'message';
  if (item.type === 'alert') return 'alert';
  // RAD-e (MAF + ADR) hits share a single visual category so the
  // dropdown only ever renders one "Additional Data" section even
  // when both lookups resolved with data.
  if (item.type === 'rade-maf') return 'rade-maf';
  if (item.type === 'rade-adr') return 'rade-adr';
  const kind = item.data.kind;
  if (kind === 'hydrant' || kind === 'parcel') return kind;
  return (item.data.attributes?._sourceLayer as string) || kind;
}

/** Build a PreparedItem for a single result, including distance from map center. */
function prepareItem(
  item: SearchResultItem,
  mapCenter: MapCenter,
  hasCenter: boolean,
): PreparedItem {
  if (item.type === 'axon') {
    const officer = item.data.attributes?.officer;
    const label = officer?.full_name || officer?.last_name || 'Axon Officer';
    const parts: string[] = [];
    const badge = officer?.badge_number;
    if (badge) parts.push(`Badge: ${badge}`);
    let dist = Infinity;
    let distLabel = '';
    if (hasCenter) {
      const coords = item.data.shape?.features?.[0]?.geometry?.coordinates;
      if (coords?.[0] != null && coords?.[1] != null) {
        dist = haversineDistance(
          mapCenter.latitude!,
          mapCenter.longitude!,
          coords[1],
          coords[0],
        );
        distLabel = formatDistance(dist);
      }
    }
    return {
      item,
      label,
      sublabel: parts.join('  ·  '),
      distanceLabel: distLabel,
      flatIdx: 0,
      icon: 'axon',
      distance: dist,
    };
  }

  if (item.type === 'place') {
    const attrs = item.data.attributes || {};
    const shortLabel = attrs.ShortLabel as string | undefined;
    const placeAddr = attrs.Place_addr as string | undefined;
    const placeName = attrs.PlaceName as string | undefined;
    // ESRI's POI taxonomy lives in the `Type` attribute (e.g.
    // "Hospital", "Gas Station", "Hotel"). Most plain street-address
    // results leave it empty, which is exactly what we want — those
    // rows should keep their default 'place' icon.
    const placeType = attrs.Type as string | undefined;
    const addr = item.data.address;

    const placeLabel =
      shortLabel ||
      (addr.indexOf(',') > 0 ? addr.slice(0, addr.indexOf(',')) : addr);

    const parts: string[] = [];
    if (placeAddr) {
      parts.push(placeAddr);
    } else {
      const afterComma =
        addr.indexOf(',') > 0 ? addr.slice(addr.indexOf(',') + 1).trim() : '';
      if (afterComma) parts.push(afterComma);
    }

    let dist = Infinity;
    let distLabel = '';
    if (hasCenter && item.data.location) {
      dist = haversineDistance(
        mapCenter.latitude!,
        mapCenter.longitude!,
        item.data.location.y,
        item.data.location.x,
      );
      distLabel = formatDistance(dist);
    }
    return {
      item,
      label: placeLabel,
      sublabel: parts.join('  ·  '),
      distanceLabel: distLabel,
      flatIdx: 0,
      icon: 'place',
      // Resolve a category-specific silhouette for POIs while leaving
      // pure street-address rows on the default 'place' icon. We never
      // pass the raw address into the resolver — that risks false
      // positives like "Park Avenue" landing the park icon.
      iconUrl: resolveCategoryIcon(placeType, placeName),
      distance: dist,
    };
  }

  if (item.type === 'static' || item.type === 'dynamic') {
    const result = item.data;
    const spatialObj = result.spatialObject as Record<string, unknown>;
    const spatialAttrs = (result.spatialObject.attributes || {}) as Record<
      string,
      unknown
    >;

    // Prefer the spatial object's own identifying name/title (matches the
    // `spatialObject.name || layerData.title` convention used by
    // StaticLayerCard). Fall back to common attribute keys, then to the
    // layer's title so the row is never blank.
    const pickStr = (v: unknown): string | undefined =>
      typeof v === 'string' && v.trim() ? v : undefined;
    const labelCandidate =
      pickStr(spatialObj.name) ||
      pickStr(spatialObj.title) ||
      pickStr(spatialAttrs.title) ||
      pickStr(spatialAttrs.name) ||
      pickStr(spatialAttrs.full_name) ||
      pickStr(spatialAttrs.label) ||
      result.layerTitle;

    const subParts: string[] = [];
    if (labelCandidate !== result.layerTitle) {
      subParts.push(result.layerTitle);
    }

    // Surface the field that fuzzysort actually matched on, so users can
    // tell why a result came back. Skipped if the matched value is already
    // the row's label (would be redundant) or if there's no field name.
    if (
      result.matchedField &&
      result.matchedText &&
      result.matchedText !== labelCandidate
    ) {
      const fieldLabel = humanizeFieldPath(result.matchedField);
      subParts.push(`${fieldLabel}: ${result.matchedText}`);
    }

    const addr =
      (spatialAttrs.address as string | undefined) ||
      (spatialAttrs.location as string | undefined);
    if (addr && !subParts.includes(addr)) subParts.push(addr);

    let dist = Infinity;
    let distLabel = '';
    const objCenter = getSpatialObjectCenter(result.spatialObject);
    if (hasCenter && objCenter) {
      dist = haversineDistance(
        mapCenter.latitude!,
        mapCenter.longitude!,
        objCenter.latitude,
        objCenter.longitude,
      );
      distLabel = formatDistance(dist);
    }

    return {
      item,
      label: labelCandidate,
      sublabel: subParts.join('  ·  '),
      distanceLabel: distLabel,
      flatIdx: 0,
      icon: item.type,
      // Static/dynamic layer titles are the most reliable category
      // signal for these results; fall through to the spatial object's
      // own name as a secondary cue (e.g. "Memorial Hospital" landing
      // a hospital icon even if the layer title is generic).
      iconUrl: resolveCategoryIcon(
        result.layerTitle,
        pickStr(spatialObj.name) || pickStr(spatialObj.title),
      ),
      distance: dist,
    };
  }

  if (item.type === 'transcript') {
    const { callerId, speakerLabel, matchedText } = item.data;
    const label =
      formatInternationalPhoneNumberHuman(callerId) || 'Active Call';
    const sublabel = speakerLabel
      ? `${speakerLabel}: "${matchedText}"`
      : `"${matchedText}"`;
    return {
      item,
      label,
      sublabel,
      distanceLabel: '',
      flatIdx: 0,
      icon: 'transcript',
      // Transcript matches don't carry a centroid; sort to the bottom of the
      // bucket consistently (matches the pattern used for `call` rows).
      distance: Infinity,
    };
  }

  if (item.type === 'message') {
    const { callerId, source, matchedText } = item.data;
    const label =
      formatInternationalPhoneNumberHuman(callerId) || 'Active Call';
    // Surface which conversation surface the hit came from so the user
    // can tell e.g. an agency-share thread apart from a TF911 chat.
    const sourceLabel = MESSAGE_SOURCE_LABELS[source] || 'Message';
    const sublabel = `${sourceLabel}: "${matchedText}"`;
    return {
      item,
      label,
      sublabel,
      distanceLabel: '',
      flatIdx: 0,
      icon: 'message',
      distance: Infinity,
    };
  }

  if (item.type === 'alert') {
    const { alert, matchedField, matchedText } = item.data;
    const label =
      alert.emergency_type?.display_name ||
      alert.emergency_type?.name ||
      alert.alert_id;

    // Sublabel format: status · matched-field-context · address. Skip
    // any segment that's blank or that would just echo the headline.
    const subParts: string[] = [];
    const statusLabel = alert.status?.display_name || alert.status?.name || '';
    if (statusLabel && statusLabel !== label) subParts.push(statusLabel);

    if (matchedField && matchedText && matchedText !== label) {
      // Match-field labels from `runAlertsSearch` are already human-
      // readable, so we skip the usual `humanizeFieldPath` pass.
      subParts.push(`${matchedField}: ${matchedText}`);
    }

    const civic =
      alert.location && 'civic' in alert.location ? alert.location.civic : null;
    const addressParts: string[] = [];
    if (civic?.street_1) addressParts.push(civic.street_1);
    if (civic?.city) addressParts.push(civic.city);
    const address = addressParts.join(', ');
    if (address && !subParts.some(p => p.includes(address))) {
      subParts.push(address);
    }

    let dist = Infinity;
    let distLabel = '';
    const geodetic =
      alert.location && 'geodetic' in alert.location
        ? alert.location.geodetic
        : null;
    if (
      hasCenter &&
      geodetic &&
      geodetic.latitude != null &&
      geodetic.longitude != null
    ) {
      dist = haversineDistance(
        mapCenter.latitude!,
        mapCenter.longitude!,
        geodetic.latitude,
        geodetic.longitude,
      );
      distLabel = formatDistance(dist);
    }

    return {
      item,
      label,
      sublabel: subParts.join('  ·  '),
      distanceLabel: distLabel,
      flatIdx: 0,
      icon: 'alert',
      distance: dist,
    };
  }

  if (item.type === 'rade-maf') {
    return {
      item,
      label: `MedicAlert profile ${item.data.id}`,
      sublabel: 'View additional data',
      distanceLabel: '',
      flatIdx: 0,
      icon: 'rade-maf',
      // RAD-e rows are not geo-anchored; sort to the bottom of the
      // bucket consistently rather than fight with the place sorter.
      distance: Infinity,
    };
  }

  if (item.type === 'rade-adr') {
    const formatted =
      formatInternationalPhoneNumberHuman(item.data.id) || item.data.id;
    return {
      item,
      label: formatted,
      sublabel: 'Additional data lookup',
      distanceLabel: '',
      flatIdx: 0,
      icon: 'rade-adr',
      distance: Infinity,
    };
  }

  if (item.type === 'call') {
    const { callerId, matchedField, matchedText } = item.data;
    const label =
      formatInternationalPhoneNumberHuman(callerId) || 'Active Call';

    // When the hit came from inside the call's ADR, surface the matched
    // field so the user can tell why this call came back. Otherwise fall
    // back to the generic descriptor.
    let sublabel = 'Active 911 call';
    if (matchedField && matchedText) {
      const humanField = matchedField
        .split(' · ')
        .map(seg =>
          seg.includes('.')
            ? `${seg.split('.')[0]} · ${humanizeFieldPath(
                seg.split('.').slice(1).join('.'),
              )}`
            : seg,
        )
        .join(' · ');
      sublabel = `${humanField}: ${matchedText}`;
    }

    return {
      item,
      label,
      sublabel,
      distanceLabel: '',
      flatIdx: 0,
      icon: 'call',
      // Calls don't carry a centroid here; treat them as "no distance" so
      // they sort to the bottom of the bucket consistently.
      distance: Infinity,
    };
  }

  const attrs = (item.data.attributes || {}) as Record<string, unknown>;
  const label = getFeatureLabel(attrs, item.data.kind);
  const sublabel = getFeatureSublabel(
    attrs,
    item.data.kind,
    item.data.geometry,
    mapCenter,
  );
  let dist = Infinity;
  let distLabel = '';
  if (hasCenter && item.data.geometry?.centroid) {
    dist = haversineDistance(
      mapCenter.latitude!,
      mapCenter.longitude!,
      item.data.geometry.centroid.latitude,
      item.data.geometry.centroid.longitude,
    );
    distLabel = formatDistance(dist);
  }
  // For generic GIS hits the originating layer title (`sourceTitle` /
  // `_sourceLayer`) is the strongest category cue. Falling back to
  // `kind` covers the dedicated `hydrant` / `parcel` buckets without
  // any extra branching since their kind strings are themselves
  // category keywords.
  const sourceLayer =
    typeof attrs._sourceLayer === 'string'
      ? (attrs._sourceLayer as string)
      : undefined;
  return {
    item,
    label,
    sublabel,
    distanceLabel: distLabel,
    flatIdx: 0,
    icon: 'gis',
    iconUrl: resolveCategoryIcon(
      item.data.sourceTitle,
      sourceLayer,
      item.data.kind,
    ),
    distance: dist,
  };
}

export interface GroupedResults {
  groups: ResultGroup[];
  counts: Record<string, number>;
  categoryOrder: string[];
  availableCategories: string[];
  visibleItems: PreparedItem[];
}

/**
 * Bucket raw search results into ordered, deduplicated, distance-sorted
 * categories ready for rendering. Maintains the order categories first
 * appeared in the result stream so the UI doesn't reshuffle as new sources
 * stream in.
 */
/**
 * Lookup index for the configured priority list. Lower index = renders
 * first; categories absent from the list fall back to first-seen order
 * below all listed buckets.
 */
const PRIORITY_INDEX: Record<string, number> = (() => {
  const map: Record<string, number> = {};
  CATEGORY_PRIORITY_ORDER.forEach((cat, idx) => {
    map[cat] = idx;
  });
  return map;
})();

const UNRANKED_PRIORITY = Number.MAX_SAFE_INTEGER;

export function getCategoryOrderForResults(
  results: SearchResultItem[],
): string[] {
  // Track first-seen order so unranked categories keep a stable
  // ordering between renders (and so equally-ranked-but-unranked
  // buckets don't reshuffle as new sources stream in).
  const firstSeen = new Map<string, number>();
  for (const item of results) {
    const cat = categoryFor(item);
    if (!firstSeen.has(cat)) {
      firstSeen.set(cat, firstSeen.size);
    }
  }
  // Stable sort: priority bucket first (calls -> axon/dynamic ->
  // address/location -> parcel -> text -> ADR), then everything else
  // in the order it streamed in.
  return Array.from(firstSeen.keys()).sort((a, b) => {
    const pa = PRIORITY_INDEX[a] ?? UNRANKED_PRIORITY;
    const pb = PRIORITY_INDEX[b] ?? UNRANKED_PRIORITY;
    if (pa !== pb) return pa - pb;
    return (firstSeen.get(a) ?? 0) - (firstSeen.get(b) ?? 0);
  });
}

export default function useGroupedResults(
  results: SearchResultItem[],
  mapCenter: MapCenter,
  activeFilter: string | null,
): GroupedResults {
  const categoryOrder = useMemo(() => {
    return getCategoryOrderForResults(results);
  }, [results]);

  const grouped = useMemo(() => {
    const buckets: Record<string, PreparedItem[]> = {};
    const ensureBucket = (cat: string): PreparedItem[] => {
      if (!buckets[cat]) buckets[cat] = [];
      return buckets[cat];
    };

    const hasCenter =
      mapCenter?.latitude != null && mapCenter?.longitude != null;

    for (const item of results) {
      const cat = categoryFor(item);
      ensureBucket(cat).push(prepareItem(item, mapCenter, hasCenter));
    }

    // Deduplicate across GIS buckets: if the same label+sublabel appears
    // in multiple sublayer categories, keep only the first occurrence.
    const seen = new Set<string>();
    const NON_GIS_CATEGORIES = new Set([
      'axon',
      'place',
      'place-local',
      'place-global',
      'static',
      'dynamic',
      'call',
      'transcript',
      'message',
      'alert',
      'rade-maf',
      'rade-adr',
    ]);
    for (const cat of categoryOrder) {
      const bucket = buckets[cat];
      if (!bucket) continue;
      if (NON_GIS_CATEGORIES.has(cat)) continue;
      buckets[cat] = bucket.filter(p => {
        const key = `${p.label}\0${p.sublabel}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    const groups: ResultGroup[] = [];
    const counts: Record<string, number> = {};
    let runningIdx = 0;
    for (const cat of categoryOrder) {
      const bucket = buckets[cat];
      if (bucket && bucket.length > 0) {
        bucket.sort((a, b) => a.distance - b.distance);
        counts[cat] = bucket.length;
        if (activeFilter && activeFilter !== cat) continue;
        for (const prepared of bucket) {
          prepared.flatIdx = runningIdx++;
        }
        groups.push({
          category: cat,
          heading: FIXED_CATEGORY_LABELS[cat] || cat,
          items: bucket,
        });
      }
    }
    return { groups, counts };
  }, [results, mapCenter, activeFilter, categoryOrder]);

  const availableCategories = useMemo(
    () => categoryOrder.filter(cat => (grouped.counts[cat] ?? 0) > 0),
    [categoryOrder, grouped.counts],
  );

  const visibleItems = useMemo(
    () => grouped.groups.flatMap(g => g.items),
    [grouped],
  );

  return {
    groups: grouped.groups,
    counts: grouped.counts,
    categoryOrder,
    availableCategories,
    visibleItems,
  };
}
